import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { AppState } from '../../../../../../core/reducers';
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
import {
  Insurance,
  InsuranceOnServerCreated,
  InsurancesService,
  selectInsuranceById,
  selectInsurancesActionLoading,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { selectErrorState } from 'src/app/core/lists/_selectors/insurance.selectors';
import { InsuranceOnServerUpdated } from 'src/app/core/lists/_actions/insurance.actions';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-insurance-edit',
  templateUrl: './insurance-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceEditComponent implements OnInit, OnDestroy {
  insurance: Insurance;
  insuranceId$: Observable<number>;
  companyId: number;
  oldInsurance: Insurance;
  selectedTab = 0;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean>;
  insuranceForm: FormGroup;
  hasFormErrors = false;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  tags = new FormControl();

  constructor(
    private navigationService: NavigationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private insuranceFB: FormBuilder,
    private layoutUtilsService: LayoutUtilsService,
    private insuranceService: InsurancesService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    // this line due to make the navigation service return to insurances list if there is no ->
    // navigation stack saved before in special cases
    this.navigationService.setDefaultRoot('/lists/insurances');

    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      if (!res) {
        this.router.navigateByUrl('/lists/insurances');
      }
      this.companyId = res !== undefined ? res.companyID : 1;
    });
    this.subscriptions.push({ id: 1, sub: userSub });
    this.loading$ = this.loadingSubject.asObservable();
    this.loadingSubject.next(true);
    const routeSub = this.activatedRoute.params.subscribe((params) => {
      const id = params.id;
      if (id && id > 0) {
        const insuranceSub = this.store
          .pipe(select(selectInsuranceById(id)))
          .subscribe((result) => {
            if (!result) {
              this.router.navigateByUrl('/lists/insurances');
              // this.loadInsuranceFromService(id);
              // return;
            }

            this.loadInsurance(result);
          });
        this.subscriptions.push({ id: 2, sub: insuranceSub });
      } else {
        const newInsurance = new Insurance();
        newInsurance.clear();
        this.loadInsurance(newInsurance);
      }
    });
    this.subscriptions.push({ id: 3, sub: routeSub });
  }

  loadInsurance(_insurance, fromService = false) {
    if (!_insurance) {
      this.goBack();
      return;
    }
    this.insurance = _insurance;
    this.insuranceId$ = of(_insurance.id);
    this.oldInsurance = Object.assign({}, _insurance);
    this.initInsurance();
    if (fromService) {
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.cdr.detach();
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  initInsurance() {
    this.createForm();
    this.loadingSubject.next(false);
  }

  createForm() {
    this.insuranceForm = this.insuranceFB.group({
      name: [this.insurance.name, Validators.required],
      insuranceType: [this.insurance.insurance_type, Validators.required],
      submissionType: [this.insurance.submission_type, Validators.required],
      bin: [this.insurance.bin, Validators.required],
      pcn: [this.insurance.pcn],
      phone: [
        this.insurance.phone !== '' && this.insurance.phone !== null
          ? '(' +
            this.insurance.phone.substring(0, 3) +
            ') ' +
            this.insurance.phone.substring(3, 6) +
            '-' +
            this.insurance.phone.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
    });
  }

  goBack() {
    this.loadingSubject.next(false);
    this.navigationService.goBack();
  }

  get f() {
    return this.insuranceForm.controls;
  }

  
  reset() {
    this.insurance = Object.assign({}, this.oldInsurance);
    this.createForm();
    this.hasFormErrors = false;
    this.insuranceForm.markAsPristine();
    this.insuranceForm.markAsUntouched();
    this.insuranceForm.updateValueAndValidity();
  }

  onSumbit() {
    this.hasFormErrors = false;
    const controls = this.insuranceForm.controls;
    if (this.insuranceForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      this.selectedTab = 0;
      return;
    }
    const editedInsurance = this.prepareInsurance();

    if (editedInsurance.id > 0) {
      this.updateInsurance(editedInsurance);
      return;
    }

    this.addInsurance(editedInsurance);
  }

  prepareInsurance(): Insurance {
    const controls = this.insuranceForm.controls;
    const _insurance = new Insurance();
    _insurance.clear();
    _insurance.id = this.insurance.id;
    _insurance.name = controls.name.value;
    _insurance.bin = controls.bin.value;
    _insurance.pcn = controls.pcn.value;
    _insurance.insuranceType = controls.insuranceType.value;
    _insurance.submissionType = controls.submissionType.value;
    _insurance.phone =
      controls.phone.value !== null
        ? controls.phone.value.replace(/\D/g, '')
        : controls.phone.value;
    for (const [key, value] of Object.entries(_insurance)) {
      if (typeof value === 'string') {
        _insurance[key] = value.trim();
      }
    }
    return _insurance;
  }

  addInsurance(_insurance: Insurance) {
    this.loadingSubject.next(true);
    this.store.dispatch(new InsuranceOnServerCreated({ insurance: _insurance }));
    if (this.subscriptions.find((sub) => sub.id === 4) === undefined) {
      const actionLoadingSubscription = this.store
        .pipe(select(selectInsurancesActionLoading))
        .subscribe((res) => {
          if (res !== true) {
            this.loadingSubject.next(false);
            if (this.subscriptions.find((sub) => sub.id === 5) === undefined) {
              const errorStateSubscription = this.store
                .pipe(select(selectErrorState))
                .subscribe((error) => {
                  if (error) {
                    if (error.error && error.error.errors) {
                      this.submissionErrorObj = error.error.errors;
                    } else {
                      this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                    }
                    this.hasSubmissionErrors = true;
                  } else if (this.router.routerState.snapshot.url !== '/lists/insurances') {
                    this.submissionErrorObj = null;
                    this.hasSubmissionErrors = false;
                    const message = this.translate.instant('INSURANCE.ADDED');
                    this.layoutUtilsService.showActionNotification(
                      message,
                      MessageType.Create,
                      3000,
                      false,
                      false,
                    );
                    this.goBack();
                  }
                });
              this.subscriptions.push({ id: 5, sub: errorStateSubscription });
            }
          }
          this.cdr.detectChanges();
        });
      this.subscriptions.push({ id: 4, sub: actionLoadingSubscription });
    }
  }

  updateInsurance(_insurance: Insurance) {
    this.loadingSubject.next(true);

    const updateInsurance: Update<Insurance> = {
      id: _insurance.id,
      changes: _insurance,
    };

    this.store.dispatch(
      new InsuranceOnServerUpdated({
        partialInsurance: updateInsurance,
        insurance: _insurance,
      }),
    );
    if (this.subscriptions.find((sub) => sub.id === 6) === undefined) {
      const actionLoadingSubscription = this.store
        .pipe(select(selectInsurancesActionLoading))
        .subscribe((res) => {
          if (res !== true) {
            this.loadingSubject.next(false);
            if (this.subscriptions.find((sub) => sub.id === 7) === undefined) {
              const errorStateSubscription = this.store
                .pipe(select(selectErrorState))
                .subscribe((error) => {
                  if (error) {
                    if (error.error && error.error.errors) {
                      this.submissionErrorObj = error.error.errors;
                    } else {
                      this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                    }
                    this.hasSubmissionErrors = true;
                  } else if (this.router.routerState.snapshot.url !== '/lists/insurances') {
                    this.goBack();
                    const message = this.translate.instant('INSURANCE.UPDATED');
                    this.layoutUtilsService.showActionNotification(
                      message,
                      MessageType.Update,
                      3000,
                      false,
                      false,
                    );
                  }
                });
              this.subscriptions.push({ id: 7, sub: errorStateSubscription });
            }
          }
          this.cdr.detectChanges();
        });
      this.subscriptions.push({ id: 6, sub: actionLoadingSubscription });
    }
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.insuranceForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  getTitle() {
    let result = this.translate.instant('INSURANCE.NEW_INSURANCE');
    if (!this.insurance || !this.insurance.id) {
      return result;
    }
    result = this.translate.instant('INSURANCE.EDIT_INSURANCE') + '-' + `${this.insurance.id}`;
    return result;
  }
}
